import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	DEFAULT_INDICATE_TIMEOUT_S,
	DeviceCategory,
	VelavuAPI,
	VelavuDevice,
} from "velavu-js-api";
import { CategoryTag, GroupTag } from "../../../../elements/categorization-tag";
import DynamicBattery from "../../../../elements/dynamic-battery";
import CooldownButton from "../../../../elements/cooldown-button";
import InlineTextArea from "../../../../elements/inline-text-area";
import VelavuSimpleTable, {
	CenteredTD,
	MultilineTD,
} from "../../../../elements/velavu-simple-table";
import { getIndicateIcon } from "../../../../helper/locate-helper";
import {
	mapDeviceModel,
	mapIndicateMethodButton,
} from "../../../../helper/language-helper";
import * as styles from "./device-properties.module.scss";
import DetailedStatusRow from "../../../../elements/detailed-status-row";
import DeviceIcon from "../../../../elements/device-icon";
import { useVelavuToast } from "../../../../elements/velavu-toast";
import ToastNotification from "../../../../elements/toast-notification";
import { useDeviceModels } from "../../../main/device-model-provider";
import { useHasPermission } from "../../../sign-in-context/sign-in-context";
import { getPermissionDeniedMessage } from "../../../../helper/permission-helper";
import { useGlobalEmitter } from "../../../../emitter/emitter-context";

export interface DevicePropertiesProps {
	device: VelavuDevice;
}

export default function DeviceProperties(props: DevicePropertiesProps) {
	const { t } = useTranslation();
	const emitter = useGlobalEmitter();

	const canIndicate = useHasPermission("devices:control");
	const canWriteAssets = useHasPermission("assets:write");
	const canWriteDevices = useHasPermission("devices:write");

	const { getDeviceModel } = useDeviceModels();
	const deviceModel = getDeviceModel(props.device?.model);
	const indicateMethod = deviceModel?.indicator;

	const locateLabel = useMemo(() => {
		if (indicateMethod === undefined) {
			return undefined;
		}
		return mapIndicateMethodButton(indicateMethod, t);
	}, [indicateMethod, t]);

	const { pushToast } = useVelavuToast();
	const locateDevice = useCallback(async () => {
		if (props.device === undefined || indicateMethod === undefined) {
			return;
		}

		try {
			await VelavuAPI.devices.indicateDevice(props.device.id, {
				timeout_s: DEFAULT_INDICATE_TIMEOUT_S,
			});
		} catch (error) {
			pushToast((close) => (
				<ToastNotification
					type="error"
					title={t("dashboard.device.indicate_failed_title")}
					onDismiss={close}
				>
					{t("dashboard.device.indicate_failed_body")}
				</ToastNotification>
			));
		}
	}, [indicateMethod, props.device, pushToast, t]);

	const handleNotesSaveError = useCallback(() => {
		pushToast((close) => (
			<ToastNotification
				type="error"
				title={t("dashboard.device.notes_save_failed_title")}
				onDismiss={close}
			>
				{t("dashboard.device.notes_save_failed_body")}
			</ToastNotification>
		));
	}, [pushToast, t]);

	const showAsAssetDetail =
		props.device.asset !== undefined &&
		props.device.category === DeviceCategory.Tag;

	const propsDeviceID = props.device.id;
	const saveDeviceNotes = useCallback(
		async (notes: string) => {
			try {
				const updatedDevice =
					await VelavuAPI.devices.modifySpecificDevice(
						propsDeviceID,
						{ notes },
					);

				emitter.emit("device", {
					id: updatedDevice.id,
					data: updatedDevice,
				});
			} catch (error) {
				console.warn("Failed to save device notes", error);
				handleNotesSaveError();
			}
		},
		[propsDeviceID, emitter, handleNotesSaveError],
	);

	const propsAssetID = props.device.asset?.id;
	const saveAssetNotes = useCallback(
		async (notes: string) => {
			if (propsAssetID === undefined) {
				throw new Error(
					"Cannot save notes for a device without an asset",
				);
			}

			try {
				const updatedAsset = await VelavuAPI.assets.modifySpecificAsset(
					propsAssetID,
					{ notes },
				);

				emitter.emit("asset", {
					id: updatedAsset.id,
					data: updatedAsset,
				});
			} catch (error) {
				console.warn("Failed to save asset notes", error);
				handleNotesSaveError();
			}
		},
		[emitter, propsAssetID, handleNotesSaveError],
	);

	const notesResetKey = showAsAssetDetail
		? `${props.device.id}:asset:${props.device.asset?.id}`
		: `${props.device.id}:device`;

	return (
		<div className={styles.container}>
			<div className={styles.tableContainer}>
				{props.device.asset !== undefined && showAsAssetDetail && (
					<VelavuSimpleTable>
						<tbody>
							<tr>
								<td>
									{t("dashboard.asset_properties.category")}
								</td>
								<td>
									<CategoryTag
										category={props.device.asset.category}
									/>
								</td>
							</tr>
							<tr>
								<td>{t("dashboard.asset_properties.group")}</td>
								<td>
									<GroupTag
										categorization={props.device.asset}
									/>
								</td>
							</tr>
							<tr>
								<td>{t("dashboard.asset_properties.notes")}</td>
								<MultilineTD>
									<InlineTextArea
										key={notesResetKey}
										value={props.device.asset.notes}
										canEdit={canWriteAssets === true}
										placeholder={t("common.none")}
										ariaLabel={t(
											"dashboard.asset_properties.notes",
										)}
										onSave={saveAssetNotes}
									/>
								</MultilineTD>
							</tr>
						</tbody>
					</VelavuSimpleTable>
				)}
				<VelavuSimpleTable>
					<tbody>
						{showAsAssetDetail && (
							<>
								<tr>
									<td>
										{t("dashboard.asset_properties.device")}
									</td>
									<CenteredTD>
										<DeviceIcon
											style={{ marginRight: 4 }}
											model={props.device.model}
										/>
										{mapDeviceModel(props.device.model, t)}
									</CenteredTD>
								</tr>
								<tr>
									<td>
										{t("dashboard.asset_properties.tag_id")}
									</td>
									<td>{props.device.id}</td>
								</tr>
							</>
						)}
						<tr>
							<td>{t("dashboard.asset_properties.status")}</td>
							<td>
								<DetailedStatusRow device={props.device} />
							</td>
						</tr>
						<tr>
							<td>{t("dashboard.asset_properties.battery")}</td>
							<CenteredTD>
								<span style={{ marginRight: 4 }}>
									{props.device.state?.power
										?.battery_level !== undefined
										? `${props.device.state.power.battery_level}%`
										: t("common.na")}
								</span>
								<DynamicBattery
									resource={props.device.state?.power}
								/>
							</CenteredTD>
						</tr>
						{!showAsAssetDetail && (
							<tr>
								<td>{t("dashboard.asset_properties.notes")}</td>
								<MultilineTD>
									<InlineTextArea
										key={notesResetKey}
										value={props.device.notes}
										canEdit={canWriteDevices === true}
										placeholder={t("common.none")}
										ariaLabel={t(
											"dashboard.asset_properties.notes",
										)}
										onSave={saveDeviceNotes}
									/>
								</MultilineTD>
							</tr>
						)}
					</tbody>
				</VelavuSimpleTable>
			</div>
			{props.device !== undefined && indicateMethod !== undefined && (
				<CooldownButton
					cooldown={DEFAULT_INDICATE_TIMEOUT_S * 1000}
					title={locateLabel}
					icon={getIndicateIcon(indicateMethod)}
					onPress={locateDevice}
					cooldownKey={props.device.id}
					disabled={!canIndicate}
					tooltip={
						!canIndicate
							? getPermissionDeniedMessage("devices:control", t)
							: undefined
					}
				/>
			)}
		</div>
	);
}
